import {
    Avatar,
    Chip,
    Icon,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserDto, UserRole } from "../../api/identity";
import { UserStatus } from "../../api/psc";
import { toJalaliDate } from "../../utils/date";

export type ManagedUser = UserDto & { activationStatus?: UserStatus };

export default function User(props: {
    user: ManagedUser;
    pending: boolean;
    onActivationChange: (user: ManagedUser, active: boolean) => void;
}) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
    const menuId = `user-actions-${props.user.id}`;
    const menuButtonId = `${menuId}-button`;
    const isMenuOpen = menuAnchor !== null && !props.pending;
    const isOwner = props.user.role === UserRole.ADMINISTRATOR;
    const isActive = props.user.activationStatus === UserStatus.ACTIVE;
    const hasActivationStatus = props.user.activationStatus !== undefined && Object.values(UserStatus).includes(props.user.activationStatus);

    const goToUpdateUser = () => {
        setMenuAnchor(null);
        navigate(`/user/update/${props.user.id}`);
    };

    const goToDeleteUser = () => {
        setMenuAnchor(null);
        navigate(`/user/delete/${props.user.id}`);
    };

    const changeActivation = (active: boolean) => {
        setMenuAnchor(null);
        props.onActivationChange(props.user, active);
    };

    return (
        <Paper sx={{ width: "100%" }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ p: 1 }}
            >
                <Avatar src={props.user.avatarUrl} />

                <Stack spacing={1} flexGrow={1} minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.primary" sx={{ overflowWrap: "anywhere" }}>
                            {props.user.displayName}
                        </Typography>

                        {props.user.role === UserRole.ADMINISTRATOR && (
                            <Chip
                                size="small"
                                color="success"
                                label="صاحب حساب"
                            />
                        )}
                    </Stack>

                    <Chip size="small" sx={{ alignSelf: "flex-start" }}
                        color={isActive ? "success" : "default"}
                        label={!hasActivationStatus ? "وضعیت نامشخص" : isActive ? "فعال" : props.user.activationStatus === UserStatus.DISABLED ? "غیرفعال" : "در انتظار فعال‌سازی"} />

                    <Typography color="text.secondary" fontSize="smaller">
                        آخرین تاریخ ورود:{" "}
                        {toJalaliDate(props.user.lastLoginDate) ?? "-"}
                    </Typography>
                </Stack>

                <IconButton
                    id={menuButtonId}
                    onClick={event => setMenuAnchor(event.currentTarget)}
                    disabled={props.pending}
                    aria-label={`عملیات ${props.user.displayName}`}
                    aria-controls={isMenuOpen ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen ? "true" : undefined}
                >
                    <Icon>more_vert</Icon>
                </IconButton>
                <Menu
                    id={menuId}
                    anchorEl={menuAnchor}
                    open={isMenuOpen}
                    onClose={() => setMenuAnchor(null)}
                    MenuListProps={{ "aria-labelledby": menuButtonId }}
                >
                    <MenuItem onClick={goToUpdateUser} disabled={props.pending}>
                        <ListItemIcon><Icon>edit</Icon></ListItemIcon>
                        <ListItemText>ویرایش</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => changeActivation(!isActive)} disabled={isOwner || props.pending || !hasActivationStatus}>
                        <ListItemIcon><Icon>{isActive ? "person_off" : "person"}</Icon></ListItemIcon>
                        <ListItemText>{isActive ? "غیرفعال کردن" : "فعال کردن"}</ListItemText>
                    </MenuItem>
                    {!isOwner && props.user.activationStatus === UserStatus.NOT_ACTIVE && (
                        <MenuItem onClick={() => changeActivation(false)} disabled={props.pending}>
                            <ListItemIcon><Icon>person_off</Icon></ListItemIcon>
                            <ListItemText>غیرفعال کردن</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem onClick={goToDeleteUser} disabled={props.pending} sx={{ color: "error.main" }}>
                        <ListItemIcon><Icon color="error">delete</Icon></ListItemIcon>
                        <ListItemText>حذف</ListItemText>
                    </MenuItem>
                </Menu>
            </Stack>
        </Paper>
    );
}
